// features/ups/modals/AgencyDrawer.js
// Right-side slide-over showing an agency’s contextual details (Accounts tab now; Settings placeholder)
/*Phase 8 agency management drawer. Renders a right-side slide-over for a selected agency, providing contextual management without leaving the Manage Agencies table. Includes:
- Contracts tab (default): UPS-admin contract control plane for the agency, allowing contract creation, enable/disable, and per-site permission management.
- Accounts tab: Lists agency user accounts (agency_* roles only) with delete actions.
- Settings tab: Reserved placeholder for future agency configuration.*/

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X, MoreHorizontal } from 'lucide-react';

import {
  fetchAccounts,
  deleteAccount as deleteAccountThunk,
} from '../../agencyAccounts/agencyAccountsSlice';

import { loadMyCompanySites, selectCompanySites } from '../../sites/sitesSlice';

import {
  fetchContracts,
  createContract,
  setContractStatus,
  fetchContractSites,
  setContractSiteStatus,
  selectContractForAgency,
  selectContractSitesByContractId,
  selectContractsState,
  clearContractsErrors,
} from '../../contracts/contractsSlice';

import { fetchContractSiteImpactApi } from '../../../services/contractService';


// Small inline kebab menu
function RowMenu({ onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-app-muted transition hover:bg-app-surface hover:text-app-text focus:outline-none focus:ring-2 focus:ring-app-primary/20"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreHorizontal size={18} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-1 w-40 overflow-hidden rounded-xl border border-app-border bg-app-card p-1 text-app-text shadow-lg"
        >
          <button
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onDelete?.();
            }}
            className="w-full rounded-lg px-3 py-2 text-left text-sm font-semibold text-red-600 transition hover:bg-red-50"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

export default function AgencyDrawer({ isOpen, onClose, agency, onAddAccount }) {
  const dispatch = useDispatch();
  const { list, loading, error } = useSelector((s) => s.agencyAccounts);

  // 1) Default tab is Contracts (Phase 8 permission plane)
  const [tab, setTab] = useState('contracts'); // 'contracts' | 'accounts' | 'settings'

  const agId = agency?.id;
  const agName = agency?.name;

  // Contracts + sites selectors
  const sites = useSelector(selectCompanySites);
  const contractsState = useSelector(selectContractsState);
  const contract = useSelector(selectContractForAgency(agId));
  const contractSites = useSelector(selectContractSitesByContractId(contract?.id));

  // Contracts UI-only state
  const [editSitesOpen, setEditSitesOpen] = useState(false);
  const [draftSiteStatus, setDraftSiteStatus] = useState({});

// Impact-confirm state (re-enable flow)
// impactConfirm: { contractId, siteId, nextStatus, impact: { total, requested, confirmed, active } }
const [impactConfirm, setImpactConfirm] = useState(null);
// pendingSiteSave: { contractId, siteId, nextStatus }
const [pendingSiteSave, setPendingSiteSave] = useState(null);



  // Fetch accounts when drawer opens (Accounts tab uses this cache)
  useEffect(() => {
    if (!isOpen) return;
    if (!agId && !agName) return;
    dispatch(fetchAccounts({ agencyId: agId }));
  }, [dispatch, isOpen, agId, agName]);

  // 2) Load contracts + sites when Contracts tab opens
  useEffect(() => {
    if (!isOpen) return;
    if (tab !== 'contracts') return;
    if (!agId) return;

    dispatch(clearContractsErrors());
    dispatch(loadMyCompanySites());
    dispatch(fetchContracts());
  }, [dispatch, isOpen, tab, agId]);

  // Load contractSites rows for this contract when contract exists
  useEffect(() => {
    if (!isOpen) return;
    if (tab !== 'contracts') return;
    if (!contract?.id) return;

    dispatch(fetchContractSites({ contractId: contract.id }));
  }, [dispatch, isOpen, tab, contract?.id]);

  // Clear confirm state if contract context changes
useEffect(() => {
  setImpactConfirm(null);
  setPendingSiteSave(null);
}, [contract?.id]);


  // Build local draft map when contractSites changes (checkbox UI)
  useEffect(() => {
    if (!contract?.id) return;

    const next = {};
    for (const r of contractSites || []) {
      const sid = r.site_id ?? r.siteId ?? r.id;
      const status = String(r.status ?? 'disabled').trim().toLowerCase();
      if (sid != null) next[String(sid)] = status;
    }
    setDraftSiteStatus(next);
  }, [contract?.id, contractSites]);

  // Reset UI-only state when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setTab('contracts'); // 1) Reset to Contracts, not Accounts
      setEditSitesOpen(false);
      setDraftSiteStatus({});

      // reset confirm flow state
      setImpactConfirm(null);
      setPendingSiteSave(null);
    }
  }, [isOpen]);

  // Client-side filter to ensure only this agency’s accounts show
  const rows = useMemo(() => {
    let r = list || [];
    if (agId || agName) {
      const idStr = agId != null && agId !== '' ? String(agId) : null;
      const nameStr = agName ? agName.trim().toLowerCase() : null;

      r = r.filter((acc) => {
        const accId = acc.agencyId ?? acc.agency_id ?? acc.agency?.id;
        const accName = (acc.agency_name ?? acc.agency?.name ?? '').trim().toLowerCase();
        if (idStr && accId != null && String(accId) === idStr) return true;
        if (nameStr && accName && accName === nameStr) return true;
        return false;
      });

      // Hide UPS roles in agency-scoped view
      r = r.filter((acc) => acc.role?.startsWith('agency'));
    }
    return r;
  }, [list, agId, agName]);

  // Persisted DB status lookup (authoritative)
// Key: siteId -> 'active' | 'disabled' (lowercased)
const contractSitesBySiteId = useMemo(() => {
  const map = {};
  for (const r of contractSites || []) {
    const sid = r.site_id ?? r.siteId ?? r.id;
    if (sid == null) continue;

    const status = String(r.status ?? r.contract_site_status ?? '').trim().toLowerCase();
    if (status) map[String(sid)] = status;
  }
  return map;
}, [contractSites]);


  // Helpers
  const allowedCount = useMemo(() => {
    return Object.values(draftSiteStatus || {}).filter((s) => s === 'active').length;
  }, [draftSiteStatus]);

  // Option 3: Explicit semantics for Allowed Sites
  // - Configured: what the contract_sites config currently says (draftSiteStatus)
  // - Operational: what is actually usable now (gated by contract.status, or backend field)
  const contractStatus = (contract?.status || '').toLowerCase();
  const contractIsActive = contractStatus === 'active';

  const configuredAllowedCount = allowedCount;

  const operationalAllowedCount =
    typeof contract?.operational_allowed_sites_count === 'number'
      ? contract.operational_allowed_sites_count
      : (contractIsActive ? configuredAllowedCount : 0);

  const onCreateContract = () => {
    if (!agId) return;
    dispatch(createContract({ agencyId: agId }));
  };

  const onToggleSiteDraft = (siteId) => {
    const key = String(siteId);
    setDraftSiteStatus((prev) => ({
      ...prev,
      [key]: prev[key] === 'active' ? 'disabled' : 'active',
    }));
  };

  const closeEditSites = () => {
  setEditSitesOpen(false);
  setImpactConfirm(null);
  setPendingSiteSave(null);
};


const onSaveSiteStatus = async (siteId) => {
  if (!contract?.id) return;

  const key = String(siteId);

  const nextStatus = String(draftSiteStatus[key] ?? 'disabled').trim().toLowerCase();
  const currentStatus = String(contractSitesBySiteId[key] ?? '').trim().toLowerCase(); // persisted

  // Only gate: persisted disabled -> user re-enables to active
  const isReEnable = currentStatus === 'disabled' && nextStatus === 'active';

  if (!isReEnable) {
    dispatch(setContractSiteStatus({ contractId: contract.id, siteId, status: nextStatus }));
    return;
  }

  const activeContractId = contract.id; // capture for race protection

  try {
    // impact check (do NOT block save if it fails)
    const open = await fetchContractSiteImpactApi(activeContractId, siteId);

    // If contract changed while request was in flight, ignore result
    if (contract?.id !== activeContractId) return;

    const openTotal =
      (open?.requested ?? 0) + (open?.confirmed ?? 0) + (open?.active ?? 0);

    if (openTotal > 0) {
      setPendingSiteSave({ contractId: activeContractId, siteId, nextStatus });
      setImpactConfirm({ contractId: activeContractId, siteId, nextStatus, impact: open });
      return; // pause save until user confirms
    }

    // no impact → save normally
    dispatch(setContractSiteStatus({ contractId: activeContractId, siteId, status: nextStatus }));
  } catch (e) {
    // impact endpoint fails → do not block save
    dispatch(setContractSiteStatus({ contractId: activeContractId, siteId, status: nextStatus }));
  }
};


  if (!isOpen) return null;

  return (
    <div className="absolute inset-x-0 bottom-0 top-[var(--ups-shell-header-h)] z-40">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <aside className="absolute right-0 top-0 flex h-full w-full flex-col bg-app-card text-app-text shadow-xl sm:w-[520px] md:w-[640px]">
        {/* Header (sticky) */}
        <div className="sticky top-0 flex items-center justify-between border-b border-app-border bg-app-card p-4">
          <h3 className="text-lg font-semibold truncate">{agency?.name}</h3>
          <button onClick={onClose} className="rounded-xl p-2 text-app-muted transition hover:bg-app-surface hover:text-app-text" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Tabs: Contracts, Accounts, Settings */}
        <div className="px-4 pt-3">
          <div className="flex gap-2 mb-3">
            <button
              onClick={() => setTab('contracts')}
              className={`rounded-xl border px-4 py-2 text-sm font-semibold transition ${
                tab === 'contracts' ? 'border-app-primary bg-app-primary text-white shadow-sm' : 'border-app-border bg-app-card text-app-muted hover:border-app-primary/40 hover:bg-app-surface hover:text-app-text'
              }`}
            >
              Contract
            </button>

            <button
              onClick={() => setTab('accounts')}
              className={`rounded-xl border px-4 py-2 text-sm font-semibold transition ${
                tab === 'accounts' ? 'border-app-primary bg-app-primary text-white shadow-sm' : 'border-app-border bg-app-card text-app-muted hover:border-app-primary/40 hover:bg-app-surface hover:text-app-text'
              }`}
            >
              Accounts
            </button>

            <button
              onClick={() => setTab('settings')}
              className={`rounded-xl border px-4 py-2 text-sm font-semibold transition ${
                tab === 'settings' ? 'border-app-primary bg-app-primary text-white shadow-sm' : 'border-app-border bg-app-card text-app-muted hover:border-app-primary/40 hover:bg-app-surface hover:text-app-text'
              }`}
            >
              Settings
            </button>
          </div>
        </div>

        {/* Content (scrollable) */}
        <div className="px-4 pb-20 overflow-y-auto flex-1">
          {/* 3B) Contracts tab */}
          {tab === 'contracts' && (
            <div className="p-3 space-y-4">
              {/* Contract card */}
              <div className="rounded-2xl border border-app-border bg-app-card p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold">Contract</div>
                    <div className="text-sm text-app-muted">
                      Controls whether this agency is permitted (and where).
                    </div>
                  </div>

                  {contract ? (
                    <span
                      className={`rounded-lg border px-2 py-1 text-xs font-semibold ${
                        contract.status === 'active'
                          ? 'border-app-primary bg-app-surface text-app-accent'
                          : 'border-app-border bg-app-surface text-app-muted'
                      }`}
                    >
                      {contract.status === 'active' ? 'Active' : 'Disabled'}
                    </span>
                  ) : (
                    <span className="rounded-lg border border-yellow-200 bg-yellow-50 px-2 py-1 text-xs font-semibold text-yellow-800">
                      No contract
                    </span>
                  )}
                </div>

                {(contractsState.loading || contractsState.sitesLoading) && (
                  <div className="mt-3 text-sm text-app-muted">Loading…</div>
                )}

                {(contractsState.error || contractsState.sitesError || contractsState.mutateError) && (
                  <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
                    {contractsState.error || contractsState.sitesError || contractsState.mutateError}
                  </div>
                )}

                <div className="mt-4 space-y-3">
                  {/* Allowed sites */}
                  <div className="flex items-center justify-between">
                    <div className="text-sm">
                      <div className="font-medium">Allowed sites</div>

                      {contract ? (
                        <div className="text-app-muted">
                          <div>{operationalAllowedCount} allowed</div>
                          <div className="text-xs text-app-muted">
                            Configured: {configuredAllowedCount}
                            {!contractIsActive ? ' (contract disabled)' : ''}
                          </div>
                        </div>
                      ) : (
                        <div className="text-app-muted">—</div>
                      )}
                    </div>

                    <button
                      disabled={!contract}
                      onClick={() => setEditSitesOpen(true)}
                      className={`rounded-xl border border-app-border bg-app-card px-4 py-2.5 text-sm font-semibold text-app-text transition ${
                        contract ? 'hover:bg-app-surface' : 'cursor-not-allowed opacity-50'
                      }`}
                    >
                      Edit sites
                    </button>
                  </div>

                  {/* Contract status */}
                  <div className="flex items-center justify-between">
                    <div className="text-sm">
                      <div className="font-medium">Contract status</div>
                      <div className="text-app-muted">
                        {contract ? 'Enable/disable this agency globally' : 'Create a contract to enable this agency'}
                      </div>
                    </div>

                    {contract ? (
                      <button
                        disabled={contractsState.mutateLoading}
                        onClick={() => {
                          const next = contract.status === 'active' ? 'disabled' : 'active';
                          dispatch(setContractStatus({ contractId: contract.id, status: next }));
                        }}
                        className="rounded-xl bg-app-primary px-5 py-2.5 text-sm font-bold text-white shadow-sm transition disabled:opacity-60"
                      >
                        {contract.status === 'active' ? 'Disable' : 'Enable'}
                      </button>
                    ) : (
                      <button
                        disabled={contractsState.mutateLoading}
                        onClick={onCreateContract}
                        className="rounded-xl bg-app-primary px-5 py-2.5 text-sm font-bold text-white shadow-sm transition disabled:opacity-60"
                      >
                        Create contract
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Edit sites modal (simple) */}
              {editSitesOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                  <div className="absolute inset-0 bg-black/30" onClick={closeEditSites} />
                  <div className="relative mx-4 w-full max-w-lg rounded-2xl border border-app-border bg-app-card text-app-text shadow-xl">
                    {/* Nested confirm overlay (re-enable with impact) */}
{impactConfirm && (
  <div className="absolute inset-0 z-[60] flex items-center justify-center">
    <div className="absolute inset-0 bg-black/40" />
    <div className="relative mx-4 w-full max-w-md rounded-2xl border border-app-border bg-app-card p-4 text-app-text shadow-xl">
      <div className="font-semibold">Re-enable site?</div>
      <div className="mt-1 text-sm text-app-muted">
        Re-enabling this site will restore access and may re-open work.
      </div>

      <div className="mt-3 space-y-1 text-sm">
        <div className="flex justify-between">
          <span className="text-app-muted">Requested</span>
          <span className="font-medium">{impactConfirm.impact?.requested ?? 0}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-app-muted">Confirmed</span>
          <span className="font-medium">{impactConfirm.impact?.confirmed ?? 0}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-app-muted">Active</span>
          <span className="font-medium">{impactConfirm.impact?.active ?? 0}</span>
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={() => {
            setImpactConfirm(null);
            setPendingSiteSave(null);
          }}
          className="rounded-xl border border-app-border bg-app-card px-4 py-2.5 text-sm font-semibold text-app-text transition hover:bg-app-surface"
        >
          Cancel
        </button>

        <button
            type="button"
            onClick={() => {
              if (!pendingSiteSave) return;

              // If contract changed since the impact was fetched, abort safely
              if (!contract?.id || contract.id !== pendingSiteSave.contractId) {
                setImpactConfirm(null);
                setPendingSiteSave(null);
                return;
              }

              const action = pendingSiteSave; // capture before clearing state

              // close overlay immediately
              setImpactConfirm(null);
              setPendingSiteSave(null);

              // run the original save (against the original contract)
              dispatch(
                setContractSiteStatus({
                  contractId: action.contractId,
                  siteId: action.siteId,
                  status: action.nextStatus,
                })
              );
            }}
            className="rounded-xl bg-app-primary px-5 py-2.5 text-sm font-bold text-white shadow-sm transition"
          >
            Re-enable
          </button>


      </div>
    </div>
  </div>
)}

                    <div className="flex items-center justify-between border-b border-app-border p-4">
                      <div className="font-semibold">Edit allowed sites</div>
                      <button
                        onClick={closeEditSites}
                        className="rounded-xl p-2 text-app-muted transition hover:bg-app-surface hover:text-app-text"
                        aria-label="Close"
                      >
                        <X size={18} />
                      </button>
                    </div>

                    <div className="p-4 max-h-[60vh] overflow-y-auto space-y-2">
                      {!sites?.length ? (
                        <div className="text-sm text-app-muted">No sites loaded.</div>
                      ) : (
                        sites.map((s) => {
                          const sid = s.id ?? s.site_id ?? s.siteId;
                          const key = sid != null ? String(sid) : '';
                          const status = key ? draftSiteStatus[key] ?? 'disabled' : 'disabled';
                          const isActive = status === 'active';

                          return (
                            <div key={key || s.name} className="flex items-center justify-between gap-3 border-b border-app-border py-3">
                              <div className="min-w-0">
                                <div className="text-sm font-medium truncate">{s.name ?? `Site ${key}`}</div>
                                <div className="text-xs text-app-muted">{isActive ? 'Allowed' : 'Blocked'}</div>
                              </div>

                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => sid != null && onToggleSiteDraft(sid)}
                                  className="rounded-xl border border-app-border bg-app-card px-3 py-2 text-sm font-semibold text-app-text transition hover:bg-app-surface"
                                >
                                  Toggle
                                </button>
                                <button
                                  type="button"
                                  disabled={!contract?.id || contractsState.mutateLoading}
                                  onClick={() => sid != null && onSaveSiteStatus(sid)}
                                  className="rounded-xl bg-app-primary px-4 py-2 text-sm font-bold text-white shadow-sm transition disabled:opacity-60"
                                >
                                  Save
                                </button>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>

                    <div className="flex justify-end border-t border-app-border p-4">
                      <button
                        onClick={closeEditSites}
                        className="rounded-xl border border-app-border bg-app-card px-4 py-2.5 text-sm font-semibold text-app-text transition hover:bg-app-surface"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Accounts tab */}
          {tab === 'accounts' && (
            <>
              {loading && <div className="p-3 text-app-muted">Loading…</div>}
              {error && <div className="m-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{error}</div>}

              {!loading && !error && rows.length === 0 && (
                <div className="p-3 text-app-muted">No accounts for this agency</div>
              )}

              {!loading && !error && rows.length > 0 && (
                <div className="divide-y divide-app-border">
                  {rows.map((acc) => (
                    <div key={acc.id} className="flex items-center justify-between py-3">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{acc.username}</div>
                        <div className="truncate text-sm text-app-muted">{acc.email}</div>
                      </div>
                      <div className="ml-3 flex items-center gap-2">
                        <span className="whitespace-nowrap rounded-lg border border-app-border bg-app-surface px-2 py-1 text-xs font-semibold text-app-muted">
                          {acc.role}
                        </span>
                        <RowMenu onDelete={() => dispatch(deleteAccountThunk(acc.id))} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Settings tab (reserved) */}
          {tab === 'settings' && <div className="p-3 text-app-muted">Settings coming soon…</div>}
        </div>

        {/* Sticky footer action (Accounts-only) */}
        {tab === 'accounts' && (
          <div className="sticky bottom-0 border-t border-app-border bg-app-card p-3">
            <button
              onClick={onAddAccount}
              className="w-full rounded-xl bg-app-primary px-4 py-3 text-sm font-bold text-white shadow-sm transition"
            >
              Add account
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}
